import type {ContractChild, ContractParent} from "./contractCoverageData";

/**
 * Human-in-the-loop scoring model.
 *
 * Each supplier carries a weighted score (0..5) that drives the contract decision
 * threshold (>= 4 mandatory / required, >= 3 recommended, else none). That score
 * is produced from several component dimensions. Some of those dimensions are
 * data-backed (criticality, dependency, fragmentation, negotiation leverage);
 * the rest are qualitative risk dimensions a reviewer assesses by hand.
 *
 * Users can override any dimension. We recompute the weighted score as an
 * ANCHORED DELTA on top of the supplier's existing score:
 *
 *     effectiveScore = clamp( baseScore + Σ weightᵢ · (overrideᵢ − baselineᵢ) , 0, 5)
 *
 * At baseline (no overrides) the delta is zero, so the verdict exactly matches
 * the source data. Overrides move the score by a transparent, visible amount.
 *
 * ⚠ The WEIGHTS below are provisional placeholders — the source data's component
 * scores are collinear, so the original weighting cannot be recovered from the
 * data alone. Replace WEIGHTS with the figures from the methodology workbook;
 * nothing else needs to change.
 */

export type ScoreKey =
    | 'criticality'
    | 'dependency'
    | 'fragmentation'
    | 'negotiation'
    | 'commercialComplexity'
    | 'legalRisk'
    | 'ipRisk'
    | 'serviceComplexity'
    | 'disputeHistory';

export type ScoreDimension = {
    key: ScoreKey;
    label: string;
    definition: string;
    weight: number;
    /** Field on ContractChild that seeds the baseline; undefined => reviewer-assessed (neutral baseline). */
    dataField?: 'criticalityScore' | 'dependencyScore' | 'fragmentationScore' | 'negotiationScore';
};

/** Reviewer-assessed dimensions start neutral (0) so they don't shift the baseline verdict. */
export const NEUTRAL_NEW_SCORE = 0;
export const SCORE_MIN = 0;
export const SCORE_MAX = 5;
const SAVING_RATE = 0.06;

export const SCORE_DIMENSIONS: ScoreDimension[] = [
    {
        key: 'criticality', label: 'Criticality Score', dataField: 'criticalityScore', weight: 0.18,
        definition: 'How essential the supplier is to operations and continuity, and the impact on the business if supply were disrupted.',
    },
    {
        key: 'dependency', label: 'Dependency Score', dataField: 'dependencyScore', weight: 0.15,
        definition: 'How reliant we are on this supplier — its share of the category, the availability of alternatives, and how hard it would be to switch.',
    },
    {
        key: 'fragmentation', label: 'Fragmentation Score', dataField: 'fragmentationScore', weight: 0.12,
        definition: 'How spread the spend is across many entities, sites and purchase orders without a single unifying agreement.',
    },
    {
        key: 'negotiation', label: 'Negotiation Leverage Score', dataField: 'negotiationScore', weight: 0.10,
        definition: 'The strength of our negotiating position — volume concentration, available alternatives, and the supplier’s cost to lose us.',
    },
    {
        key: 'commercialComplexity', label: 'Commercial Complexity', weight: 0.10,
        definition: 'Complexity of the commercial arrangement — pricing structures, volumes, rebates and multi-business-unit terms that a formal agreement should govern.',
    },
    {
        key: 'legalRisk', label: 'Legal / Regulatory Risk', weight: 0.12,
        definition: 'Exposure to legal, regulatory or compliance obligations that a master agreement should formally cover.',
    },
    {
        key: 'ipRisk', label: 'IP / Confidentiality Risk', weight: 0.08,
        definition: 'Sensitivity of shared intellectual property or confidential data, and the exposure if it is not contractually protected.',
    },
    {
        key: 'serviceComplexity', label: 'Service / Delivery Complexity', weight: 0.08,
        definition: 'Complexity of service levels, delivery and performance management that requires clearly defined contractual terms.',
    },
    {
        key: 'disputeHistory', label: 'Dispute / Performance History', weight: 0.07,
        definition: 'Track record of disputes, claims or performance issues that warrant tighter contractual control.',
    },
];

export type ScoreOverrides = Partial<Record<ScoreKey, number>>;

const clamp = (v: number) => Math.max(SCORE_MIN, Math.min(SCORE_MAX, v));

/** The baseline (un-overridden) value for a dimension on a given supplier. */
export function baselineValue(child: ContractChild, dim: ScoreDimension): number {
    return dim.dataField ? (child[dim.dataField] as number) : NEUTRAL_NEW_SCORE;
}

/** Effective value = override if present, else baseline. */
export function effectiveValue(child: ContractChild, dim: ScoreDimension, overrides: ScoreOverrides): number {
    const o = overrides[dim.key];
    return o === undefined ? baselineValue(child, dim) : o;
}

export function effectiveWeightedScore(child: ContractChild, overrides: ScoreOverrides): number {
    let delta = 0;
    for (const dim of SCORE_DIMENSIONS) {
        delta += dim.weight * (effectiveValue(child, dim, overrides) - baselineValue(child, dim));
    }
    return Math.round(clamp(child.weightedScore + delta) * 100) / 100;
}

export type ContractNeed = 'required' | 'recommended' | 'none';

export function needFromScore(score: number): ContractNeed {
    if (score >= 4) return 'required';
    if (score >= 3) return 'recommended';
    return 'none';
}

export const VERDICT_LABEL: Record<ContractNeed, string> = {
    required: 'Mandatory contract',
    recommended: 'Recommended contract',
    none: 'No contract needed',
};

/** True when at least one dimension is overridden away from its baseline. */
export function hasOverrides(child: ContractChild, overrides: ScoreOverrides): boolean {
    return SCORE_DIMENSIONS.some(d => {
        const o = overrides[d.key];
        return o !== undefined && o !== baselineValue(child, d);
    });
}

/**
 * Recompute a child against its overrides. The weighted score always updates.
 * Contract POSITION only changes for uncontracted suppliers — a matched contract's
 * position stays utilisation-based and is not affected by the qualitative score.
 */
export function recomputeChild(child: ContractChild, overrides: ScoreOverrides): ContractChild {
    const weightedScore = effectiveWeightedScore(child, overrides);
    const out: ContractChild = {...child, weightedScore};

    if (child.contractCount > 0) return out; // contracted -> position is utilisation-driven

    const need = needFromScore(weightedScore);
    if (need === 'required') {
        out.position = 'contract_required';
        out.costSavingEur = Math.round(SAVING_RATE * child.spend);
    } else if (need === 'recommended') {
        out.position = 'contract_recommended';
        out.costSavingEur = Math.round(SAVING_RATE * child.spend);
    } else {
        out.position = 'no_contract_required';
        out.costSavingEur = 0;
    }
    out.costAvoidanceEur = 0;
    return out;
}

/** Recompute a whole parent given overrides keyed by child index, re-summing totals. */
export function recomputeParent(parent: ContractParent, overridesByIndex: Map<number, ScoreOverrides>): ContractParent {
    if (overridesByIndex.size === 0) return parent;
    const children = parent.children.map((ch, i) => {
        const o = overridesByIndex.get(i);
        return o ? recomputeChild(ch, o) : ch;
    });
    return {
        ...parent,
        children,
        costSavingEur: children.reduce((s, c) => s + c.costSavingEur, 0),
        costAvoidanceEur: children.reduce((s, c) => s + c.costAvoidanceEur, 0),
    };
}
